import React, { useState, useEffect } from "react";
import { FaBars, FaTimes } from "react-icons/fa";
import { FiSearch, FiChevronDown, FiChevronUp } from "react-icons/fi";
import { useLocation, Link } from "react-router-dom";
import logo from "../assets/logo.jpg";

const MENU = ["Home", "About", "Our Services", "Blogs", "Collaborations", "Team"];

const SERVICES_SUBMENU = [
  { label: "Architectural Design", path: "/architectural-design" },
  { label: "Interior Design", path: "/interior-design" },
  // { label: "Residential Design", path: "/residential-design" },
  { label: "Project Management Consultancy", path: "/project-management-consultancy" },
];

const Badge = ({ children }) => (
  <span className="ml-1.5 inline-flex items-center rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
    {children}
  </span>
);

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const location = useLocation();
  const isHomePage = location.pathname === "/";

  const isSolid = !isHomePage || scrolled;

  useEffect(() => {
    if (!isHomePage) {
      setScrolled(false);
      return;
    }
    const handleScroll = () => setScrolled(window.scrollY > 60);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isHomePage]);

  useEffect(() => {
    setMobileOpen(false);
    setMobileServicesOpen(false);
  }, [location.pathname]);

  const linkPath = (item) => (item === "Home" ? "/" : `/${item.toLowerCase()}`);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-[9999] transition-all duration-300 ${
        isSolid ? "bg-white shadow-md" : "bg-gradient-to-b from-black/60 via-black/30 to-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center shrink-0">
            <div
              className={`rounded-lg px-1.5 py-1 sm:px-2 transition-colors duration-300 ${
                isSolid ? "" : "bg-white/95 shadow-sm"
              }`}
            >
              <img src={logo} alt="Arch Point" className="h-9 sm:h-12 lg:h-14 w-auto" />
            </div>
          </Link>

          {/* Desktop menu */}
          <nav className="hidden lg:flex items-center gap-8 xl:gap-10">
            {MENU.map((item) => {
              const isServices = item === "Our Services";
              return (
                <div key={item} className="relative group">
                  {isServices ? (
                    <button
                      className={`flex items-center gap-1 text-sm font-semibold uppercase tracking-wide transition-colors duration-200 py-2 ${
                        isSolid
                          ? "text-gray-800 hover:text-amber-600"
                          : "text-white hover:text-amber-300 drop-shadow"
                      }`}
                    >
                      {item}
                      <FiChevronDown className="text-xs mt-0.5 transition-transform duration-200 group-hover:rotate-180" />
                    </button>
                  ) : (
                    <Link
                      to={linkPath(item)}
                      className={`inline-flex items-center text-sm font-semibold uppercase tracking-wide transition-colors duration-200 relative py-2 ${
                        isSolid
                          ? "text-gray-800 hover:text-amber-600"
                          : "text-white hover:text-amber-300 drop-shadow"
                      }`}
                    >
                      {item}
                      {item === "Collaborations" && <Badge>New</Badge>}
                      <span
                        className={`absolute left-0 bottom-0 h-0.5 w-0 transition-all duration-300 group-hover:w-full ${
                          isSolid ? "bg-amber-500" : "bg-amber-400"
                        }`}
                      />
                    </Link>
                  )}

                  {/* Services dropdown */}
                  {isServices && (
                    <div className="absolute top-full left-1/2 -translate-x-1/2 pt-3 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-300 ease-out">
                      <div className="w-72 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden py-1">
                        {SERVICES_SUBMENU.map((sub) => (
                          <Link
                            key={sub.path}
                            to={sub.path}
                            className="flex items-center px-5 py-3 text-sm font-medium text-gray-700 hover:bg-amber-50 hover:text-amber-700 transition-colors duration-200 border-b border-gray-50 last:border-none"
                          >
                            {sub.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          {/* Desktop right side (Search icon only, no CTAs) */}
          <div className="hidden lg:flex items-center gap-2">
            {/* <button
              aria-label="Search"
              className={`p-2.5 rounded-full transition-colors duration-200 ${
                isSolid
                  ? "text-gray-700 hover:bg-gray-100 hover:text-amber-600"
                  : "text-white hover:bg-white/10"
              }`}
            >
              <FiSearch className="text-lg" />
            </button> */}
          </div>

          {/* Mobile controls */}
          <div className="flex lg:hidden items-center gap-1">
            {/* <button
              aria-label="Search"
              className={`p-2 transition-colors duration-200 ${
                isSolid ? "text-gray-800" : "text-white"
              }`}
            >
              <FiSearch className="text-xl" />
            </button> */}
            <button
              aria-label="Toggle menu"
              onClick={() => setMobileOpen((v) => !v)}
              className={`p-2 text-2xl transition-colors duration-200 ${
                isSolid ? "text-gray-800" : "text-white"
              }`}
            >
              {mobileOpen ? <FaTimes /> : <FaBars />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu panel */}
      <div
        className={`lg:hidden overflow-hidden transition-[max-height] duration-300 ease-out bg-white border-t border-gray-100 shadow-xl ${
          mobileOpen ? "max-h-[80vh]" : "max-h-0"
        }`}
      >
        <div className="px-6 py-4 flex flex-col gap-1 max-h-[80vh] overflow-y-auto">
          {MENU.map((item) => {
            const isServices = item === "Our Services";
            return (
              <div key={item}>
                {isServices ? (
                  <>
                    <button
                      onClick={() => setMobileServicesOpen((v) => !v)}
                      className="w-full flex items-center justify-between py-3 text-gray-800 font-semibold border-b border-gray-50"
                    >
                      <span>{item}</span>
                      {mobileServicesOpen ? <FiChevronUp /> : <FiChevronDown />}
                    </button>
                    <div
                      className={`overflow-hidden transition-[max-height] duration-300 ease-out ${
                        mobileServicesOpen ? "max-h-96" : "max-h-0"
                      }`}
                    >
                      <div className="pl-4 py-2 flex flex-col gap-1 bg-gray-50/50 rounded-lg my-1">
                        {SERVICES_SUBMENU.map((sub) => (
                          <Link
                            key={sub.path}
                            to={sub.path}
                            className="py-2 text-sm text-gray-600 hover:text-amber-600 font-medium"
                          >
                            {sub.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </>
                ) : (
                  <Link
                    to={linkPath(item)}
                    className="flex items-center py-3 text-gray-800 font-semibold border-b border-gray-50 last:border-none hover:text-amber-600 transition-colors"
                  >
                    {item}
                    {item === "Collaborations" && <Badge>New</Badge>}
                  </Link>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </header>
  );
};

export default Navbar;